// AUD-1/AUD-2 — the synth patch model + a PURE, deterministic offline renderer.
//
// A *patch* is a small typed bundle of synth parameters (oscillator/noise source,
// ADSR envelope, a state-variable filter sweep, pitch glide/vibrato/jitter). There
// are NO audio samples anywhere in the project (D-audio: "Tone.js or any audio lib
// … never; raw WebAudio, §12") — every sound is synthesized from a patch.
//
// `renderPatch` turns a patch into a mono Float32 PCM buffer using only integer/
// float math and the seeded `Rng`, so the same (patch, opts) render byte-identical
// on every platform. That determinism is what lets the patch-golden test hash a
// rendered 100 ms sample (M23.1 Verify). The live WebAudio engine feeds these
// buffers into an `AudioBufferSourceNode`; nothing here touches WebAudio/DOM, so
// this module is import-safe from vitest and from the sim-facing boot host alike.
import { Rng } from '../../../sim/engine/rng';

/** Oscillator/noise source shapes a patch can voice. */
export type Waveform = 'sine' | 'square' | 'saw' | 'triangle' | 'noise';

/** State-variable filter output tap. */
export type FilterMode = 'lowpass' | 'highpass' | 'bandpass';

/** ADSR amplitude envelope, in milliseconds (sustain is a 0..1 level). */
export interface Envelope {
  readonly attackMs: number;
  readonly decayMs: number;
  readonly sustain: number;
  readonly releaseMs: number;
}

/** A resonant filter whose cutoff sweeps `startHz → endHz` across the note. */
export interface FilterSweep {
  readonly mode: FilterMode;
  readonly startHz: number;
  readonly endHz: number;
  /** Resonance; 0.5 = flat, higher = more peak. Clamped to a stable range. */
  readonly q?: number;
}

/** A synth patch — the complete recipe for one sound event. */
export interface Patch {
  readonly wave: Waveform;
  /** Base pitch in Hz for tonal waves (ignored for pure noise). */
  readonly freq: number;
  /** Note length in milliseconds (includes the release tail). */
  readonly durationMs: number;
  readonly env: Envelope;
  /** Peak amplitude 0..1 before positional/master gain. */
  readonly gain: number;
  readonly filter?: FilterSweep;
  /** Pitch glide: frequency is `freq · lerp(from,to)` over `glideMs`. */
  readonly pitchGlide?: { readonly from: number; readonly to: number; readonly glideMs: number };
  /** Vibrato depth (± fraction of freq) at `rateHz`. */
  readonly vibrato?: { readonly rateHz: number; readonly depth: number };
  /** Random per-play detune in cents (± this many), drawn from the play seed. */
  readonly jitterCents?: number;
  /** Layer a second detuned copy (unison) at this cents offset for body. */
  readonly detuneCents?: number;
}

/** Options for one render/play of a patch. */
export interface RenderOptions {
  /** Playback-rate multiplier (note pitch; note blocks pass `2^((n−12)/12)`). */
  readonly pitch?: number;
  /** Extra amplitude scale (0..1) applied on top of the patch gain. */
  readonly volume?: number;
  /** Seed for jitter/noise — same seed ⇒ identical buffer. */
  readonly seed?: number | string;
  /** Output sample rate. Defaults to {@link GOLDEN_SAMPLE_RATE} (golden-stable). */
  readonly sampleRate?: number;
}

/** The fixed sample rate the determinism goldens render at. */
export const GOLDEN_SAMPLE_RATE = 22050;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * clamp(t, 0, 1);
const centsToRatio = (cents: number): number => 2 ** (cents / 1200);

/** One tonal-oscillator sample for `phase` in [0,1). */
function osc(wave: Waveform, phase: number): number {
  switch (wave) {
    case 'sine': return Math.sin(phase * 2 * Math.PI);
    case 'square': return phase < 0.5 ? 1 : -1;
    case 'saw': return 2 * phase - 1;
    case 'triangle': return 4 * Math.abs(phase - 0.5) - 1;
    case 'noise': return 0; // handled specially (needs the rng, not the phase)
  }
}

/** ADSR amplitude at time `t` ms into a note of length `durMs`. */
function envAt(env: Envelope, t: number, durMs: number): number {
  const a = Math.max(0, env.attackMs);
  const d = Math.max(0, env.decayMs);
  const r = Math.max(0, env.releaseMs);
  const relStart = Math.max(a + d, durMs - r); // where the release ramp begins
  if (t < a) return a === 0 ? 1 : t / a;                       // attack 0→1
  if (t < a + d) return d === 0 ? env.sustain : lerp(1, env.sustain, (t - a) / d); // decay 1→S
  if (t < relStart) return env.sustain;                        // sustain hold
  if (t >= durMs) return 0;
  return r === 0 ? 0 : lerp(env.sustain, 0, (t - relStart) / r); // release S→0
}

/**
 * Render a patch to a mono Float32 PCM buffer. Pure and deterministic: a function
 * only of `patch`, `opts`, and the seed. Peaks are soft-clipped to [-1, 1].
 */
export function renderPatch(patch: Patch, opts: RenderOptions = {}): Float32Array {
  const sr = opts.sampleRate ?? GOLDEN_SAMPLE_RATE;
  const pitch = opts.pitch ?? 1;
  const volume = clamp(opts.volume ?? 1, 0, 1);
  const n = Math.max(1, Math.round((patch.durationMs / 1000) * sr));
  const out = new Float32Array(n);
  const rng = new Rng(opts.seed ?? 'aud');

  // Per-play detune from jitter (drawn once).
  const jitter = patch.jitterCents ? (rng.nextFloat() * 2 - 1) * patch.jitterCents : 0;
  const baseFreq = patch.freq * pitch * centsToRatio(jitter);
  const detune = patch.detuneCents ? centsToRatio(patch.detuneCents) : 0;

  // State-variable filter state (Chamberlin), if a sweep is configured.
  let lp = 0, bp = 0;
  const fq = patch.filter ? 1 / clamp(patch.filter.q ?? 0.7, 0.5, 2.0) : 0;

  let phase = 0, phase2 = 0;
  for (let i = 0; i < n; i++) {
    const tMs = (i / sr) * 1000;
    const tNorm = i / n;

    // Instantaneous frequency: base × pitch-glide × vibrato.
    let f = baseFreq;
    if (patch.pitchGlide) {
      const g = patch.pitchGlide;
      f *= lerp(g.from, g.to, g.glideMs <= 0 ? 1 : tMs / g.glideMs);
    }
    if (patch.vibrato) f *= 1 + patch.vibrato.depth * Math.sin(2 * Math.PI * patch.vibrato.rateHz * (i / sr));

    // Source sample.
    let s: number;
    if (patch.wave === 'noise') {
      s = rng.nextFloat() * 2 - 1;
    } else {
      phase += f / sr; phase -= Math.floor(phase);
      s = osc(patch.wave, phase);
      if (detune) { phase2 += (f * detune) / sr; phase2 -= Math.floor(phase2); s = (s + osc(patch.wave, phase2)) * 0.5; }
    }

    // Filter sweep.
    if (patch.filter) {
      const fc = lerp(patch.filter.startHz, patch.filter.endHz, tNorm);
      const fCo = clamp(2 * Math.sin((Math.PI * clamp(fc, 20, sr * 0.45)) / sr), 0, 0.99);
      lp += fCo * bp;
      const hp = s - lp - fq * bp;
      bp += fCo * hp;
      s = patch.filter.mode === 'lowpass' ? lp : patch.filter.mode === 'highpass' ? hp : bp;
    }

    // Envelope + gain, soft-clipped.
    const amp = envAt(patch.env, tMs, patch.durationMs) * patch.gain * volume;
    out[i] = Math.tanh(s * amp * 1.2);
  }
  return out;
}

/** A stable 32-bit FNV-1a hex hash of a PCM buffer (quantized to int16). The
 *  patch-golden test hashes a rendered sample and pins it. */
export function hashSamples(buf: Float32Array): string {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < buf.length; i++) {
    const q = clamp(Math.round(buf[i]! * 32767), -32768, 32767) & 0xffff;
    h = (Math.imul(h ^ (q & 0xff), 0x01000193)) >>> 0;
    h = (Math.imul(h ^ (q >>> 8), 0x01000193)) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
